import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Link,
  PaginationCompact,
  PaginationDisplayedItemCount,
  FormController,
  RadioGroup,
} from '@one-thd/sui-atomic-components';
import React, { useEffect, useState } from 'react';
import PaymentCard from '../../../../shared/PaymentCard';
import {
  PAGINATION_THRESHOLD,
  PAGINATION_JUMP_CONTROLS_THRESHOLD,
  PAYMENTS_PAGE_SIZE,
} from '../../../../../constants/general';
import { track } from '../../../../../analytics/analytics';
import { setPaginationAnalytics } from '../../../../../utils/analytics';
import {
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_OVERLAY,
} from '../../../../../constants/analytics';
import {
  ADD_PAYMENT,
  CHANGE_HOW_YOU_PAY,
  CONFIRM_LEAVE,
  CONFIRM_PAYMENT,
  SETUP_HOW_YOU_PAY,
} from '../../../../../models/views';
import { useDrawer } from '../../../../../hooks/use-drawer';
import { usePayments } from '../../../../../hooks/use-payments';
import { useViews } from '../../../../../hooks/use-views';
import { IPayment } from '../../../../../models/payments';

export const SelectPaymentView = () => {
  const { payments, selectedPaymentMethod, setSelectedPaymentMethod } =
    usePayments();
  const { goToView, flowName } = useViews();
  const { hideDrawer } = useDrawer();
  const [cardsPerPage, setCardsPerPage] = useState<IPayment[]>([]);
  const [page, setPage] = useState(1);
  const [isPaginationVisible, setIsPaginationVisible] = useState(true);
  const [totalPageCount, setTotalPageCount] = useState(0);

  useEffect(() => {
    if (!selectedPaymentMethod && payments) {
      setSelectedPaymentMethod(
        payments.find((card) => card.isInstantCheckoutDefault || null),
      );
    }

    const getCardsPerPage = payments?.slice(
      PAYMENTS_PAGE_SIZE * (page - 1),
      PAYMENTS_PAGE_SIZE * page,
    );

    const totalCardsCount = payments?.length;
    const getTotalPageCount = Math.ceil(totalCardsCount / PAYMENTS_PAGE_SIZE);
    const getIsPaginationVisible = totalCardsCount > PAGINATION_THRESHOLD;

    setCardsPerPage(getCardsPerPage);
    setTotalPageCount(getTotalPageCount);
    setIsPaginationVisible(getIsPaginationVisible);
  }, [payments, page, selectedPaymentMethod]);

  const handleChangePage = (event: Event, value: number) => {
    const output = {
      target: event.target as HTMLInputElement,
      value,
      overlayName: 'payment method',
      currentPage: page,
    };
    setPaginationAnalytics(output);
    setPage(value);
  };

  const onSelect = (card: IPayment | undefined) => {
    setSelectedPaymentMethod(card);
  };

  const handleClose = () => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'select payment method',
      action: 'cancel',
    });
    if (flowName === CHANGE_HOW_YOU_PAY || flowName === SETUP_HOW_YOU_PAY) {
      hideDrawer();
    } else {
      goToView(CONFIRM_LEAVE);
    }
  };

  const savePaymentMethod = () => {
    if (
      !selectedPaymentMethod ||
      selectedPaymentMethod?.cardDetails?.isExpired ||
      !selectedPaymentMethod?.billingAddress?.id
    ) {
      return;
    }

    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'select payment method',
      action: 'save changes',
    });
    goToView(CONFIRM_PAYMENT);
  };

  return (
    <div data-testid="select-payment-method-section">
      <DrawerHeader
        title="Select Payment Method"
        data-testid="drawer-header"
        onClose={() => {
          handleClose();
        }}
      />

      <DrawerBody data-testid="select-payment-method-drawer">
        <div className="sui-mb-6">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            component="button"
            data-testid="add-new-payment-method-link"
            onClick={() => {
              track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout add new payment',
              });
              goToView(ADD_PAYMENT);
            }}
          >
            Add New Payment
          </Link>
        </div>
        {payments && (
          <FormController>
            <RadioGroup
              aria-labelledby="payment-card-radio-buttons-group-label"
              name="payment-method-select-group"
              value={selectedPaymentMethod}
            >
              <ul className="sui-p-2">
                {cardsPerPage?.map((card, index) => {
                  return (
                    <li key={index}>
                      <PaymentCard
                        card={card}
                        index={index}
                        selectedPaymentMethod={selectedPaymentMethod}
                        onSelect={onSelect}
                      />
                    </li>
                  );
                })}
              </ul>
            </RadioGroup>
          </FormController>
        )}

        {isPaginationVisible && (
          <div
            className="sui-flex sui-flex-col sui-items-center sui-gap-y-2"
            data-testid="PaginationWrap"
          >
            <PaginationCompact
              count={totalPageCount}
              page={page}
              onChange={handleChangePage}
              showFirstButton={
                totalPageCount > PAGINATION_JUMP_CONTROLS_THRESHOLD
              }
              showLastButton={
                totalPageCount > PAGINATION_JUMP_CONTROLS_THRESHOLD
              }
            />
            <PaginationDisplayedItemCount
              page={page}
              itemsPerPage={PAYMENTS_PAGE_SIZE}
              items={payments?.length}
            />
          </div>
        )}
      </DrawerBody>

      <DrawerFooter>
        <div className="sui-flex sui-gap-2">
          <Button
            data-testid="cancel-button"
            fullWidth
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            data-testid="save-button"
            fullWidth
            variant="primary"
            onClick={() => {
              savePaymentMethod();
            }}
          >
            Save and Continue
          </Button>
        </div>
      </DrawerFooter>
    </div>
  );
};
